import { 
  Card, 
  CardContent, 
  Typography, 
  Box, 
  Grid, 
  LinearProgress, 
  Chip, 
  Divider
} from '@mui/material';
import {
  Timeline as ProgressIcon,
  VerifiedUser as QualityIcon,
  PlayArrow as InProgressIcon,
  CheckCircle as CompletedIcon,
  PauseCircleOutline as NotStartedIcon,
  Cancel as CancelledIcon,
  CalendarToday as DateIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  ResponsiveContainer,
  Tooltip as RechartsTooltip 
} from 'recharts';

/**
 * Tarjeta individual de análisis de proyecto
 * Muestra métricas de progreso y calidad con visualizaciones
 */
const ProjectAnalyticsCard = ({ project }) => {
  // Configuración de colores para estados
  const statusConfig = {
    not_started: { color: 'default', icon: NotStartedIcon, label: 'Sin Empezar', bgcolor: '#f5f5f5' },
    in_progress: { color: 'warning', icon: InProgressIcon, label: 'En Progreso', bgcolor: '#fff3e0' },
    completed: { color: 'success', icon: CompletedIcon, label: 'Completado', bgcolor: '#e8f5e8' },
    cancelled: { color: 'error', icon: CancelledIcon, label: 'Cancelado', bgcolor: '#ffebee' },
  };

  const status = statusConfig[project.status] || statusConfig.not_started;
  const StatusIcon = status.icon;

  // Datos para gráfico de barras comparativo
  const comparisonData = [
    {
      name: 'Progreso',
      valor: project.progressMetrics.completionPercentage,
      color: '#2196f3',
    },
    {
      name: 'Calidad',
      valor: project.qualityMetrics.successRate,
      color: '#4caf50',
    },
  ];

  return (
    <Card 
      elevation={3}
      sx={{
        height: '100%',
        border: '1px solid',
        borderColor: 'divider',
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: 6,
          transform: 'translateY(-4px)',
          borderColor: status.color === 'default' ? '#9e9e9e' : `${status.color}.main`,
        },
      }}
    >
      <CardContent sx={{ p: 2 }}>
        {/* Header del proyecto */}
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box flex={1}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 'bold',
                fontSize: '1rem',
                lineHeight: 1.2,
                mb: 0.5
              }}
            >
              {project.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {project.project_number}
            </Typography>
          </Box>
          
          <Box display="flex" alignItems="center" gap={1}>
            <Chip
              label={status.label}
              color={status.color}
              size="small"
              icon={<StatusIcon sx={{ fontSize: '0.9rem' }} />}
            />
          </Box>
        </Box>

        {/* Información básica */}
        <Box mb={2}>
          <Grid container spacing={1} alignItems="center">
            <Grid item xs={6}>
              <Box display="flex" alignItems="center" gap={0.5}>
                <DateIcon sx={{ fontSize: '0.8rem', color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  {project.start_date || 'Sin fecha'}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box display="flex" alignItems="center" gap={0.5}>
                <PersonIcon sx={{ fontSize: '0.8rem', color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  {project.subscribers?.length || 0} asignados
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Métricas principales */}
        <Grid container spacing={2} mb={2}>
          <Grid item xs={6}>
            <Box textAlign="center">
              <Typography variant="caption" color="text.secondary" display="block">
                Progreso
              </Typography>
              <Typography variant="h6" color="primary.main" sx={{ fontWeight: 'bold' }}>
                {project.progressMetrics.completionPercentage.toFixed(1)}%
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box textAlign="center">
              <Typography variant="caption" color="text.secondary" display="block">
                Calidad
              </Typography>
              <Typography variant="h6" color="success.main" sx={{ fontWeight: 'bold' }}>
                {project.qualityMetrics.successRate.toFixed(1)}%
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Gráficos de visualización */}
        <Box mb={2}>
          <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.8rem' }}>
            Distribución de Progreso
          </Typography>
          <ResponsiveContainer width="100%" height={120}>
            <BarChart data={comparisonData} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
              <XAxis dataKey="name" tick={{ fontSize: 10 }} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
              <RechartsTooltip 
                formatter={(value) => [`${value.toFixed(1)}%`, 'Valor']}
                labelFormatter={(label) => label}
              />
              <Bar dataKey="valor" fill={(entry, index) => comparisonData[index]?.color || '#8884d8'} />
            </BarChart>
          </ResponsiveContainer>
        </Box>

        {/* Detalles adicionales */}
        <Box>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Box display="flex" alignItems="center" gap={0.5}>
                <ProgressIcon sx={{ fontSize: '0.8rem', color: 'primary.main' }} />
                <Typography variant="caption">
                  {project.progressMetrics.tasksCompleted}/{project.progressMetrics.totalTasks} tareas
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box display="flex" alignItems="center" gap={0.5}>
                <QualityIcon sx={{ fontSize: '0.8rem', color: 'success.main' }} />
                <Typography variant="caption">
                  {project.qualityMetrics.completedControls}/{project.qualityMetrics.totalControls} controles
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Progress bar general */}
        <Box mt={2}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
            <Typography variant="caption" color="text.secondary">
              Progreso
            </Typography>
            <Typography variant="caption" color="primary.main" sx={{ fontWeight: 'bold' }}>
              {(() => {
                const progreso = project.progressMetrics.completionPercentage || 0;
                const calidad = project.qualityMetrics.successRate || 0;
                return ((progreso + calidad) / 2).toFixed(1);
              })()}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={(() => {
              const progreso = project.progressMetrics.completionPercentage || 0;
              const calidad = project.qualityMetrics.successRate || 0;
              return (progreso + calidad) / 2;
            })()}
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: 'grey.200',
              '& .MuiLinearProgress-bar': {
                backgroundColor: (() => {
                  const progresoGeneral = ((project.progressMetrics.completionPercentage || 0) + (project.qualityMetrics.successRate || 0)) / 2;
                  return progresoGeneral >= 80 ? '#4caf50' : 
                         progresoGeneral >= 60 ? '#ff9800' : '#f44336';
                })(),
                borderRadius: 3,
              },
            }}
          />
        </Box>
      </CardContent>
    </Card>
  );
};

export default ProjectAnalyticsCard;
